import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { AlertTriangle, Download, Loader2, Play } from 'lucide-react';
import { api } from '../lib/api';
import { readStashedPreview } from '../lib/preview-window';
import { useCsvObjectUrl } from '../lib/csv';
import { formatNumber } from '../lib/format';
import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';

/**
 * The preview as its own tab: chrome-free, full-viewport email. Opened from a
 * preview drawer, which hands over the already-rendered result — so this page
 * only re-runs the query when asked, never on load. A reload keeps working
 * because the tab holds its own copy of the handoff.
 */
export function ReportPreviewWindow() {
  const [params] = useSearchParams();
  const token = params.get('k');
  const reportId = params.get('report');
  const date = params.get('date');

  const [stashed] = useState(() => (token ? readStashedPreview(token) : null));

  const rerun = useMutation({
    mutationFn: () => {
      if (!reportId) throw new Error('No report in the URL');
      return api.previewReport(reportId, date ?? undefined);
    },
  });

  const preview = rerun.data ?? stashed;
  const csvUrl = useCsvObjectUrl(preview?.csv_base64);

  useEffect(() => {
    const label = preview?.report_name ?? preview?.report_id ?? reportId;
    document.title = label ? `Preview · ${label}` : 'Report preview';
  }, [preview, reportId]);

  if (!preview) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background p-6">
        <Card className="w-full max-w-lg">
          <CardHeader>
            <CardTitle className="text-base">Preview not in this tab</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 text-sm text-muted-foreground">
            <p>
              The rendered email is handed over when the tab is opened, so it is gone once the tab
              is closed. Run it again{date ? ` for ${date}` : ''}, or reopen the preview from the
              report page.
            </p>
            {rerun.isError && (
              <div className="flex gap-2 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-destructive">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                <p className="font-mono text-xs whitespace-pre-wrap">
                  {(rerun.error as Error).message}
                </p>
              </div>
            )}
            <div className="flex items-center gap-3">
              <Button onClick={() => rerun.mutate()} disabled={!reportId || rerun.isPending}>
                {rerun.isPending ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Running…
                  </>
                ) : (
                  <>
                    <Play className="h-4 w-4" />
                    Run preview
                  </>
                )}
              </Button>
              {reportId && <span className="font-mono text-xs">{reportId}</span>}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex h-screen flex-col bg-background">
      <header className="flex flex-wrap items-center justify-between gap-3 border-b border-line bg-panel px-4 py-2">
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold text-foreground">
            {preview.report_name || preview.report_id}
          </p>
          <p className="truncate font-mono text-xs text-muted-foreground">{preview.subject}</p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <Badge variant="outline">{preview.target_date}</Badge>
          <Badge variant={preview.row_count === 0 ? 'warning' : 'secondary'}>
            {formatNumber(preview.row_count)} rows
          </Badge>
          {csvUrl && (
            <a
              href={csvUrl}
              download={preview.csv_filename}
              className="inline-flex h-7 items-center gap-1.5 rounded-md border border-line bg-panel px-2.5 text-xs font-medium text-fg-2 transition-colors hover:bg-raise hover:text-foreground"
            >
              <Download className="h-3.5 w-3.5" />
              CSV
            </a>
          )}
        </div>
      </header>
      <div className="min-h-0 flex-1 bg-white">
        <iframe title="Email preview" srcDoc={preview.html} sandbox="" className="h-full w-full" />
      </div>
    </div>
  );
}
